import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { orgContext, setUnauthorizedHandler, tokenStorage } from '@/core/api';
import {
  normalizeRole,
  useSession,
  type CurrentUser,
} from '@/stores/session';
import * as api from './api';
import { AuthContext, type AuthStatus } from './authContext';
import { toSessionOrg } from './sessionOrg';
import type { AuthUser, GoogleLoginInput, LoginInput } from './types';

const toUser = (u: AuthUser): CurrentUser => ({
  id: u.id,
  username: u.username,
  email: u.email,
  first_name: u.first_name,
  last_name: u.last_name,
  role: u.role,
  photo: u.photo ?? null,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setSession = useSession((s) => s.setSession);
  const clearSession = useSession((s) => s.clear);

  // Start in "loading" only if we have a refresh token to bootstrap from.
  const [status, setStatus] = useState<AuthStatus>(() =>
    tokenStorage.getRefresh() ? 'loading' : 'unauthenticated',
  );

  // Fetch orgs → pick active → set the org header → fetch the user (role is
  // resolved per active org). Access token is restored via 401→refresh if needed.
  const loadSession = useCallback(async () => {
    const orgs = await api.getMyOrganizations();
    const saved = useSession.getState().activeOrgSlug; // persisted across reloads
    const active = orgs.find((o) => o.slug === saved) ?? orgs[0] ?? null;
    const activeSlug = active?.slug ?? null;
    orgContext.set(activeSlug); // set before current-user so role is org-correct
    const user = await api.getCurrentUser();
    setSession({
      user: toUser(user),
      organizations: orgs.map(toSessionOrg),
      activeOrgSlug: activeSlug,
      role: normalizeRole(user.role),
      whiteLabel: Boolean(user.white_label),
    });
  }, [setSession]);

  const signIn = useCallback(
    async (input: LoginInput) => {
      const res = await api.login(input);
      const { access, refresh } = api.tokensFrom(res);
      tokenStorage.set(access, refresh);
      await loadSession();
      setStatus('authenticated');
    },
    [loadSession],
  );

  const signInWithGoogle = useCallback(
    async (input: GoogleLoginInput) => {
      const res = await api.loginGoogle(input);
      const { access, refresh } = api.tokensFrom(res);
      tokenStorage.set(access, refresh);
      await loadSession();
      setStatus('authenticated');
    },
    [loadSession],
  );

  const signOut = useCallback(async () => {
    await api.logout();
    tokenStorage.clear();
    clearSession();
    qc.clear();
    setStatus('unauthenticated');
    navigate('/login');
  }, [clearSession, navigate, qc]);

  // Bootstrap once on mount when a refresh token exists.
  useEffect(() => {
    if (status !== 'loading') return;
    let cancelled = false;
    void (async () => {
      try {
        await loadSession();
        if (!cancelled) setStatus('authenticated');
      } catch {
        if (!cancelled) {
          tokenStorage.clear();
          clearSession();
          setStatus('unauthenticated');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, loadSession, clearSession]);

  // An unrecoverable 401 (refresh failed) → clean sign-out + redirect.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      tokenStorage.clear();
      clearSession();
      setStatus('unauthenticated');
      navigate('/login');
    });
  }, [clearSession, navigate]);

  const value = useMemo(
    () => ({ status, signIn, signInWithGoogle, signOut }),
    [status, signIn, signInWithGoogle, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
